/* lang.ts — Phase 4e (IT/EN parity).
 *
 * Single source of truth for the app-wide language preference. ForecastItaly
 * owns the persisted state (lang lives inside fonio_forecast_v9 along with
 * the rest of the dashboard model), so this hook reads from there and
 * stays in sync via:
 *   - storage events (cross-tab — user toggles lang in tab A, tab B updates)
 *   - a custom `forecast-lang-change` window event dispatched same-tab
 *     whenever ForecastItaly mutates state.lang (so admin pages re-render
 *     without a reload).
 *
 * Why not a React context: the legacy ForecastItaly state machine lives
 * outside the v2 React tree (it's wrapped by App.tsx but stores its own
 * useFonioState model). A context would have to be plumbed through
 * either ForecastItaly's setState callbacks or a top-level App wrapper,
 * both of which touch hot paths. A localStorage-backed hook with explicit
 * publish is the minimum-blast-radius design.
 *
 * Defaults to "it" to match DEFAULT_STATE.lang in lib/model.ts.
 */
import React from "react";

export type Lang = "it" | "en";

const STORAGE_KEY = "fonio_forecast_v9";
const EVENT_NAME = "forecast-lang-change";
// PR 2 — default flipped from "it" to "en" per Marco's feedback. Existing
// users (whose persisted state has lang: "it") are unaffected — the default
// only kicks in for fresh loads / cleared storage. The dashboard text uses
// English by default for a wider professional audience; Italian remains
// available via the LangMenu in the topbar.
const DEFAULT_LANG: Lang = "en";

/** Read the current lang preference from persisted state. Safe in SSR. */
export function getLang(): Lang {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_LANG;
    const parsed = JSON.parse(raw);
    const v = parsed?.lang;
    return v === "it" || v === "en" ? v : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

/** Publish a language change so other components (admin views, primitives)
 *  re-render. Call from ForecastItaly's setState({ lang }) callback. The
 *  hook subscribes to this event AND to native storage events for cross-tab
 *  sync. Also publishes window.__APP_LANG__ for primitives' fallback path
 *  (Modal, ErrorState) that don't take a `lang` prop. */
export function publishLang(lang: Lang) {
  if (typeof window === "undefined") return;
  try { (window as any).__APP_LANG__ = lang; } catch {}
  try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: lang })); } catch {}
}

/** Phase B3 — set the app-wide language from anywhere (topbar, sidebar,
 *  future settings page). Patches the `lang` slot inside the persisted
 *  ForecastItaly state in localStorage, then broadcasts via publishLang
 *  so all subscribers (including ForecastItaly itself) re-render.
 *
 *  ForecastItaly's own lang Segmented continues to use setTop({lang}) —
 *  that path is unchanged and remains the canonical "in-app" setter
 *  inside the state machine. setLang() is the cross-surface bridge so
 *  callers outside ForecastItaly's React tree can drive the value.
 *
 *  Race note: localStorage writes are synchronous; the broadcast fires
 *  before any React render scheduled by the call site. ForecastItaly's
 *  subscriber (added in this phase) reads the new value off the event
 *  and patches state.lang on the next render, keeping in-memory state
 *  consistent with the persisted value. */
export function setLang(lang: Lang) {
  if (typeof window === "undefined") return;
  if (lang !== "it" && lang !== "en") return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === "object") {
      parsed.lang = lang;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } else {
      // No persisted state yet (fresh load) — write a minimal object.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ lang }));
    }
  } catch {
    // localStorage can throw in incognito / quota; fall through to broadcast
    // so the in-memory state still updates. Persistence will retry on the
    // next mutation that goes through ForecastItaly's autosave.
  }
  publishLang(lang);
}

/** React hook — returns the current lang, re-renders on change. */
export function useLang(): Lang {
  const [lang, setLang] = React.useState<Lang>(() => getLang());

  React.useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === "it" || detail === "en") setLang(detail);
      else setLang(getLang()); // re-read on any change event without payload
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLang(getLang());
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return lang;
}

/** Pick the right string based on current lang. Handy for inline:
 *    {t(lang, "Save", "Salva")}
 *  vs the more verbose:
 *    {lang === "it" ? "Salva" : "Save"}
 *  Pick whichever reads better at the call site; both are equivalent. */
export function t(lang: Lang, en: string, it: string): string {
  return lang === "it" ? it : en;
}
